/* eslint-disable */
import { ComponentRef, forwardRef, useState, useEffect, useRef } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import ReactMarkdown from 'react-markdown'
import EnterpriseIcon, { ProfileIcon } from '@enterprise-ui/icons'
import Loading from './Loading'
import apiConfig from '../apiConfig'
import { trackInteraction, trackLinkClick } from '../utils/trackEvent'
import { ThumbsDown, ThumbsUp } from 'lucide-react'
import { themeColors } from '../theme'
import '../Welcome.scss'
// import ProfileIconWithColor from '../ProfileIconWithColor'

interface Message {
  role: string
  content: string
  timeTaken?: number
  isComplete?: boolean
  conversationId?: string
  feedback?: 'up' | 'down' | null
  question?: string
  timestamp?: number
}

interface MessagesProps {
  messages: Message[]
  fontSize: number
  loading: boolean
  onSuggestedQuestion: (question: string | undefined) => void
}

const markdownStyles = {
  p: {
    margin: 0,
    padding: 0,
    lineHeight: 1.6,
  },
  pre: {
    margin: 0,
    padding: '8px',
  },
  code: {
    margin: 0,
    padding: '2px 4px',
  },
} as const

const Messages = forwardRef<ComponentRef<typeof motion.div>, MessagesProps>(
  function Messages({ messages, fontSize, loading, onSuggestedQuestion }, ref) {
    const [darkMode, setDarkMode] = useState<boolean>(false)
    // useEffect(() => {
    //   const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    //   setDarkMode(mediaQuery.matches)
    //   const handleChange = (e: MediaQueryListEvent) => {
    //     setDarkMode(e.matches)
    //   }
    //   mediaQuery.addEventListener('change', handleChange)
    //   return () => mediaQuery.removeEventListener('change', handleChange)
    // }, [])
    const assistantMessageRef = useRef<HTMLDivElement>(null)
    const messageRefs = useRef<Map<number, HTMLDivElement>>(new Map())

    // Move to prod by removing perf from hrccb.perf.target.com
    const baseUrl =
      apiConfig.chatApi?.gatewayBaseUrl || 'https://hrccb.target.com'

    useEffect(() => {
      if (assistantMessageRef.current) {
        assistantMessageRef.current.focus()
      }
    }, [messages])
    const colors = {
      text: themeColors.text,
      aiBackground: themeColors.assistantBubble,
      guestBackground: themeColors.userBubble,
      link: themeColors.link,
      feedbackActive: themeColors.primary,
      feedbackInactive: themeColors.feedbackInactive,
    }

    const messageStyles = {
      messageContainer: {
        padding: '24px',
        wordBreak: 'break-word',
        marginBottom: '8px',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
        transition: 'all 0.2s ease',
      },
      userMessage: {
        backgroundColor: colors.guestBackground,
        borderRadius: '18px 4px 18px 18px',
        color: colors.text,
      },
      assistantMessage: {
        backgroundColor: colors.aiBackground,
        borderRadius: '4px 15px 15px 15px',
        color: colors.text,
        border: '1px solid rgba(0, 0, 0, 0.1)',
      },
      messageGroup: {
        display: 'flex',
        marginBottom: '10px',
        maxWidth: '80%',
      },
      avatar: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '32px',
        height: '32px',
        borderRadius: '50%',
        marginBottom: '4px',
        marginRight: '10px',
        color: 'var(--target-primary)',
      },
      timeContainer: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        fontSize: '14px',
        marginTop: '4px',
        width: '100%',
        lineHeight: '18px',
      },
      timeTaken: {
        color: themeColors.standardText,
        fontWeight: '500',
      },
      feedbackButtons: {
        display: 'flex',
        gap: '8px',
        alignItems: 'center',
      },
      feedbackButton: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'none',
        border: 'none',
        padding: '4px',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        color: '#666666',
      },
    } as const

    const [, setRenderTrigger] = useState(0)
    const [clickedQuestions, setClickedQuestions] = useState<Set<number>>(
      new Set(),
    )
    const messageRenderTimes = useRef(new Map<number, Date>())

    const handleQuestionClick = (
      index: number,
      question: string | undefined,
    ) => {
      if (question) {
        trackInteraction(question, 'suggested_question')
      }
      setClickedQuestions((prev) => new Set([...prev, index]))
      onSuggestedQuestion(question)
    }

    const handleFeedback = async (
      messageIndex: number,
      type: 'up' | 'down',
    ) => {
      trackInteraction(`Thumbs ${type}`, 'feedback_button')
      const message = messages[messageIndex]
      if (message.feedback === type) return
      try {
        const response = await fetch(
          `${apiConfig.chatApi?.host}${apiConfig.chatApi?.feedbackEndpoint}`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              model_response: message.content,
              feedback: type === 'up' ? 'good' : 'bad',
              conversation_id: message?.conversationId,
            }),
          },
        )
        if (!response.ok) {
          throw new Error('Failed to submit feedback')
        }
        messages[messageIndex] = { ...message, feedback: type }
        setRenderTrigger((prev) => prev + 1)
      } catch (error) {
        console.error('Error submitting feedback:', error)
      }
    }

    useEffect(() => {
      const hasIncompleteMessages = messages.some((msg) => !msg.isComplete)
      if (hasIncompleteMessages) {
        const interval = setInterval(() => {
          setRenderTrigger((prev) => prev + 1)
        }, 1000)
        return () => clearInterval(interval)
      }
    }, [messages])

    useEffect(() => {
      messageRefs.current.clear()
    }, [messages])

    return (
      <motion.div
        layoutScroll
        style={
          {
            flexGrow: 1,
            overflow: 'auto',
            padding: '1rem',
          } as React.CSSProperties
        }
        ref={ref}
      >
        <motion.div
          {...({
            role: 'log',
            'aria-live': 'polite',
            'aria-relevant': 'additions',
            'aria-atomic': 'false',
            style: {
              margin: '0 auto',
              display: 'flex',
              flexDirection: 'column',
              gap: '0.5rem',
              fontSize: `${fontSize}px`,
            } as React.CSSProperties,
          } as any)}
        >
          <AnimatePresence mode={'popLayout'}>
            {messages?.map((msg, index) => {
              if (!messageRenderTimes.current.has(index)) {
                messageRenderTimes.current.set(index, new Date())
              }
              const renderTime = messageRenderTimes.current.get(index)!

              return (
                <motion.div
                  key={index}
                  ref={(el) => {
                    if (el)
                      messageRefs.current.set(
                        index,
                        el as unknown as HTMLDivElement,
                      )
                  }}
                  {...({
                    tabIndex: 0,
                    'aria-label': `Chat message from ${msg.role === 'user' ? 'you' : 'AI assistant'}`,
                    role: 'region',
                    'aria-live':
                      msg.role === 'assistant' ? 'polite' : undefined,
                    onKeyDown: (e: any) => {
                      if (
                        e.key === 'ArrowUp' &&
                        messageRefs.current.has(index - 1)
                      ) {
                        e.preventDefault()
                        messageRefs.current.get(index - 1)?.focus()
                      } else if (
                        e.key === 'ArrowDown' &&
                        messageRefs.current.has(index + 1)
                      ) {
                        e.preventDefault()
                        messageRefs.current.get(index + 1)?.focus()
                      }
                    },
                    style: {
                      ...messageStyles.messageGroup,
                      marginRight: msg.role === 'user' ? '0' : 'auto',
                      marginTop: index === 0 ? '2rem' : '0',
                      alignSelf:
                        msg.role === 'user' ? 'flex-end' : 'flex-start',
                      flexDirection:
                        msg.role === 'user' ? 'row-reverse' : 'row',
                    } as React.CSSProperties,
                    initial: { opacity: 0, y: 10 },
                    animate: { opacity: 1, y: 0 },
                    exit: { opacity: 0, y: 0 },
                  } as any)}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      justifyContent: 'center',
                      width: '32px',
                      height: '32px',
                      minWidth: '32px',
                      minHeight: '32px',
                      marginLeft: msg.role === 'user' ? '10px' : '0',
                      marginRight: msg.role !== 'user' ? '10px' : '0',
                      marginTop: '4px',
                    }}
                  >
                    {msg.role === 'user' ? (
                      <EnterpriseIcon
                        icon={ProfileIcon}
                        style={{
                          width: '32px',
                          height: '32px',
                          color: themeColors.userAvatarProfile,
                        }}
                      />
                    ) : (
                      // <EnterpriseIcon icon={ProfileIcon} style={{ width: '32px', height: '32px', color: themeColors.userAvatarProfile }} />
                      <img
                        src={`${baseUrl}/Bullseye_Red_CMYK.svg`}
                        alt="Target AI Assistant Avatar"
                        style={{ width: '32px', height: '32px' }}
                      />
                    )}
                  </div>

                  <div
                    style={
                      {
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems:
                          msg.role === 'user' ? 'flex-end' : 'flex-start',
                      } as React.CSSProperties
                    }
                  >
                    <div
                      style={{ display: 'inline-block' } as React.CSSProperties}
                    >
                      {msg.role === 'assistant' && !msg.content ? (
                        <div
                          style={
                            {
                              padding: '8px',
                            } as React.CSSProperties
                          }
                        >
                          <div
                            aria-live="polite"
                            aria-atomic="true"
                            style={{
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                              height: '100%',
                              visibility: loading ? 'visible' : 'hidden',
                              color: themeColors.standardText,
                            }}
                          >
                            <i>Writing Message...</i>
                          </div>
                        </div>
                      ) : (
                        <div
                          ref={
                            msg.role === 'assistant' &&
                            index === messages.length - 1
                              ? assistantMessageRef
                              : null
                          }
                          tabIndex={-1}
                          aria-live="polite"
                          aria-atomic="true"
                          role="status"
                          style={{
                            ...messageStyles.messageContainer,
                            ...(msg.role === 'user'
                              ? messageStyles.userMessage
                              : messageStyles.assistantMessage),
                            fontSize: `${fontSize}px`,
                            outline: '1px solid none',
                            boxShadow: '0 0 0 2px none',
                          }}
                          onFocus={(e) => {
                            e.target.style.outline = '1px solid red'
                            e.target.style.boxShadow = '0 0 0 2px white'
                          }}
                          onBlur={(e) => {
                            e.target.style.outline = 'none'
                            e.target.style.boxShadow = 'none'
                          }}
                        >
                          <ReactMarkdown
                            components={{
                              p: ({ ...props }) => (
                                <p
                                  style={
                                    {
                                      ...markdownStyles.p,
                                      color: colors.text,
                                    } as React.CSSProperties
                                  }
                                  {...props}
                                />
                              ),
                              pre: ({ ...props }) => (
                                <pre style={markdownStyles.pre} {...props} />
                              ),
                              code: ({ ...props }) => (
                                <code style={markdownStyles.code} {...props} />
                              ),
                              a: ({ ...props }) => (
                                <a
                                  style={
                                    {
                                      textDecoration: 'underline',
                                      fontWeight: 'bold',
                                      color: colors.link,
                                    } as React.CSSProperties
                                  }
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={(e) => {
                                    trackLinkClick(
                                      e.currentTarget.textContent || '',
                                      e.currentTarget.href || '',
                                    )
                                  }}
                                  {...props}
                                />
                              ),
                            }}
                          >
                            {msg.content}
                          </ReactMarkdown>
                        </div>
                      )}
                      {!loading && (
                        <div
                          style={
                            messageStyles.timeContainer as React.CSSProperties
                          }
                        >
                          <span
                            style={
                              messageStyles.timeTaken as React.CSSProperties
                            }
                          >
                            <i>
                              {renderTime.toLocaleTimeString([], {
                                hour: '2-digit',
                                minute: '2-digit',
                                hour12: true,
                              })}
                            </i>
                          </span>
                          {msg.role === 'assistant' && index !== 0 && (
                            <div
                              role="group"
                              aria-label="Provide feedback"
                              style={
                                messageStyles.feedbackButtons as React.CSSProperties
                              }
                            >
                              <button
                                onClick={() => handleFeedback(index, 'up')}
                                style={{
                                  ...messageStyles.feedbackButton,
                                  color:
                                    msg.feedback === 'up'
                                      ? colors.feedbackActive
                                      : colors.feedbackInactive,
                                }}
                                aria-label="Thumbs up"
                                type="button"
                                tabIndex={0}
                              >
                                <ThumbsUp size={20} aria-hidden="true" />
                              </button>

                              <button
                                onClick={() => handleFeedback(index, 'down')}
                                style={{
                                  ...messageStyles.feedbackButton,
                                  color:
                                    msg.feedback === 'down'
                                      ? colors.feedbackActive
                                      : colors.feedbackInactive,
                                }}
                                aria-label="Thumbs down"
                                type="button"
                                tabIndex={0}
                              >
                                <ThumbsDown size={20} aria-hidden="true" />
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    {msg.role === 'assistant' &&
                      msg.isComplete &&
                      msg.question &&
                      !clickedQuestions.has(index) && (
                        <motion.div
                          initial={{ opacity: 0, y: 5 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -5 }}
                          style={
                            {
                              display: 'flex',
                              flexDirection: 'column',
                              gap: '4px',
                              marginTop: '8px',
                            } as React.CSSProperties
                          }
                        >
                          <button
                            onClick={() =>
                              handleQuestionClick(index, msg.question)
                            }
                            style={
                              {
                                background: themeColors.background,
                                border: `1px solid ${themeColors.suggestedChatText}`,
                                borderRadius: '20px',
                                padding: '8px 16px',
                                color: themeColors.suggestedChatText,
                                fontSize: '14px',
                                cursor: 'pointer',
                                width: 'fit-content',
                                transition: 'all 0.2s ease',
                              } as React.CSSProperties
                            }
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background =
                                themeColors.suggestedChatText
                              e.currentTarget.style.color =
                                themeColors.background
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background =
                                themeColors.background
                              e.currentTarget.style.color =
                                themeColors.suggestedChatText
                            }}
                          >
                            You might want to ask: {msg.question}
                          </button>
                        </motion.div>
                      )}
                  </div>
                </motion.div>
              )
            })}
          </AnimatePresence>
        </motion.div>
      </motion.div>
    )
  },
)

export default Messages
